// lib/services/compute/color_gpu_slot.ts

import BaseSlot, { SlotState } from "../lib/services/compute/base_slot";

/**
 * Per-frame compute workspace for the sticker detection pipeline.
 * Holds all GPU buffers and textures that StickerCompute writes into
 * during one frame's dispatch.
 *
 * Lifecycle: Idle -> Recording (compute dispatching) -> Ready (results
 * available for readback) -> Leased (readback in flight) -> Ready/Idle.
 *
 * Pool policy: PostReleasePolicy.KeepReady — the readback consumer can
 * re-lease the same slot if no newer compute has finished.
 */
export default class ColorGpuSlot extends BaseSlot {
  // Frame dimensions — set once at allocate time.
  width: number = 0;
  height: number = 0;
  size: number = 0; // width * height

  // Constants from StickerCompute.
  static readonly BLACKWHITE_MASK_DIVS = 40;
  static readonly MAX_STICKERS = 256;
  static readonly MAX_BLOBS = 256;
  static readonly MASK_RES = 3;
  static readonly MAX_BLOBS_PER_LINE = 16;

  // --- Buffers -----------------------------------------------------------

  info_buffer: GPUBuffer | null = null;
  blob_basic_buffer: GPUBuffer | null = null;
  blob2_buffer: GPUBuffer | null = null;
  blob2_collect_buffer: GPUBuffer | null = null;
  bwgrid_buffer: GPUBuffer | null = null;
  nngrid_buffer: GPUBuffer | null = null;
  sticker_buffer: GPUBuffer | null = null;
  quad_param_buffer: GPUBuffer | null = null;
  scene_cbcr_buffer: GPUBuffer | null = null;      // float2 per block
  scene_cbcr_avg_buffer: GPUBuffer | null = null;   // float2 x 1
  display_info_buffer: GPUBuffer | null = null; 
  packed_count_buffer:  GPUBuffer | null = null; // 2 int
  packed_blobs_buffer: GPUBuffer | null = null; 
  // --- Textures ----------------------------------------------------------

  black_white_mask_texture: GPUTexture | null = null;
  black_white_mask_texture_view: GPUTextureView | null = null;

  black_white_mask_texture_read: GPUTexture | null = null;
  black_white_mask_texture_view_read: GPUTextureView | null = null;

  blob_debug_texture: GPUTexture | null = null;
  blob_debug_texture_view: GPUTextureView | null = null;

  // --- Sticker buffer size (for readback) --------------------------------

  sticker_buffer_size: number = 0;
  blob_buffer_size: number = 0;

  constructor() {
    super(false);
  }

  /**
   * Allocate all GPU resources for this slot. Called once during pool
   * initialization. The slot owns the resources and destroys them on
   * destroy_resources().
   */
  allocate_resources(
    device: GPUDevice,
    key: string,
    slot_index: number,
    width: number,
    height: number,
    nngrid_size: number,
    sticker_buffer_size: number,
    blob_buffer_size: number
  ): void {
    this.width = width;
    this.height = height;
    this.size = width * height;
    this.sticker_buffer_size = sticker_buffer_size;
    this.blob_buffer_size = blob_buffer_size;

    const label = `${key}_s${slot_index}`;

    // --- Buffers --------------------------------------------------------

    // Info buffer is initialized with GridInfo data by the caller after
    // allocation. We just create the storage here.
    this.info_buffer = device.createBuffer({
      label: `${label}_info`,
      size: 64, // GridInfo struct size — caller may override via writeBuffer
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    this.blob_basic_buffer = device.createBuffer({
      label: `${label}_blob_basic`,
      size: this.size * 16,
      usage: GPUBufferUsage.STORAGE,
    });

    this.blob2_buffer = device.createBuffer({
      label: `${label}_blob2`,
      size: blob_buffer_size,//ColorGpuSlot.MAX_BLOBS * 288,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });

    this.blob2_collect_buffer = device.createBuffer({
      label: `${label}_blob2_collect`,
      size: ColorGpuSlot.MAX_BLOBS_PER_LINE * height * 280,
      usage: GPUBufferUsage.STORAGE,
    });

    this.bwgrid_buffer = device.createBuffer({
      label: `${label}_bwgrid`,
      size: ColorGpuSlot.BLACKWHITE_MASK_DIVS * ColorGpuSlot.BLACKWHITE_MASK_DIVS * 16,
      usage: GPUBufferUsage.STORAGE,
    });

    this.nngrid_buffer = device.createBuffer({
      label: `${label}_nngrid`,
      size: nngrid_size * 4,
      usage: GPUBufferUsage.STORAGE,
    });

    this.sticker_buffer = device.createBuffer({
      label: `${label}_sticker`,
      size: sticker_buffer_size,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });

    this.quad_param_buffer = device.createBuffer({
      label: `${label}_quad_param`,
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const block_count = ColorGpuSlot.BLACKWHITE_MASK_DIVS * ColorGpuSlot.BLACKWHITE_MASK_DIVS;

    this.scene_cbcr_buffer = device.createBuffer({
      label: `${label}_scene_cbcr`,
      size: block_count * 8, // float2 per block
      usage: GPUBufferUsage.STORAGE,
    });

    this.scene_cbcr_avg_buffer = device.createBuffer({
      label: `${label}_scene_cbcr_avg`,
      size: 8, // single float2
      usage: GPUBufferUsage.STORAGE,
    });


  this.display_info_buffer = device.createBuffer({
      label: `${label}_display_info`,
      size: 32, // VideoDisplayInfo
      usage: GPUBufferUsage.STORAGE| GPUBufferUsage.COPY_DST,
    });

    this.packed_count_buffer = device.createBuffer({
      label: `${label}_dpacked_count`,
      size: 16, // DrawIndirectArgs
      usage: GPUBufferUsage.STORAGE| GPUBufferUsage.COPY_DST | GPUBufferUsage.INDIRECT
    });

    this.packed_blobs_buffer = device.createBuffer({
      label: `${label}_packed_blobs`,
      size: ColorGpuSlot.MAX_BLOBS *32, // PackedBlob
      usage: GPUBufferUsage.STORAGE| GPUBufferUsage.COPY_DST | GPUBufferUsage.VERTEX,
    });


    // --- Textures -------------------------------------------------------

    const tex_usage =
      GPUTextureUsage.STORAGE_BINDING |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST |
      GPUTextureUsage.RENDER_ATTACHMENT;

    this.black_white_mask_texture = device.createTexture({
      label: `${label}_bw_mask`,
      size: [width, height],
      format: "rgba8unorm",
      usage: tex_usage,
    });
    this.black_white_mask_texture_view = this.black_white_mask_texture.createView();

    this.black_white_mask_texture_read = device.createTexture({
      label: `${label}_bw_mask_read`,
      size: [width, height],
      format: "rgba8unorm",
      usage: tex_usage,
    });
    this.black_white_mask_texture_view_read = this.black_white_mask_texture_read.createView();

    this.blob_debug_texture = device.createTexture({
      label: `${label}_blob_debug`,
      size: [width, height],
      format: "rgba8unorm",
      usage: tex_usage,
    });
    this.blob_debug_texture_view = this.blob_debug_texture.createView();
  }

  // --- State transitions -------------------------------------------------

  begin_record(): boolean {
    if (this.state !== SlotState.Idle) return false;
    this.transition_to(SlotState.Recording);
    return true;
  }

  mark_ready(sequence: number, timestamp_sec: number): void {
    this.sequence = sequence;
    this.timestampSec = timestamp_sec;
    this.transition_to(SlotState.Ready);
  }

  cancel_record(): void {
    if (this.state === SlotState.Recording) {
      this.transition_to(SlotState.Idle);
    }
  }

  // --- Lifecycle ---------------------------------------------------------

  recycle(): void {
    super.recycle();
  }

  destroy_resources(): void {
    this.info_buffer?.destroy();
    this.blob_basic_buffer?.destroy();
    this.blob2_buffer?.destroy();
    this.blob2_collect_buffer?.destroy();
    this.bwgrid_buffer?.destroy();
    this.nngrid_buffer?.destroy();
    this.sticker_buffer?.destroy();
    this.quad_param_buffer?.destroy();

    this.black_white_mask_texture?.destroy();
    this.black_white_mask_texture_read?.destroy();
    this.blob_debug_texture?.destroy();

    this.info_buffer = null;
    this.blob_basic_buffer = null;
    this.blob2_buffer = null;
    this.blob2_collect_buffer = null;
    this.bwgrid_buffer = null;
    this.nngrid_buffer = null;
    this.sticker_buffer = null;
    this.quad_param_buffer = null;
    this.display_info_buffer = null; 
    this.packed_count_buffer = null; 
    this.packed_blobs_buffer = null; 

    this.black_white_mask_texture = null;
    this.black_white_mask_texture_view = null;
    this.black_white_mask_texture_read = null;
    this.black_white_mask_texture_view_read = null;
    this.blob_debug_texture = null;
    this.blob_debug_texture_view = null;

    

    this.width = 0;
    this.height = 0;
    this.size = 0;
  }
}